/**
 * Calendar history: the undo/redo stack plus the JSON API and grid-refresh
 * helpers every calendar interaction (modal, quick add, drag, bulk bar)
 * goes through. Commands are applied first and pushed afterwards, so a push
 * never re-runs the action; undo() calls cmd.undo(), redo() calls cmd.do().
 */
import { newStyleIndexes } from './head-styles.js';
import { planRefresh } from './refresh-plan.js';

function enc(id) {
    return encodeURIComponent(id);
}

function csrf() {
    return window.getCSRFToken ? window.getCSRFToken() : '';
}
// 이 탭의 id: 서버가 변경을 브로드캐스트할 때 실어 보내 자기 에코를 걸러낸다.
export const CLIENT_ID = 'c' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);

// POST a form body and parse the JSON answer. Snapshots (URLSearchParams)
// are cloned so a stored command can be replayed any number of times.
function send(url, body) {
    let payload = body;
    if (body instanceof URLSearchParams) {
        payload = new URLSearchParams(body);
    } else if (!(body instanceof FormData)) {
        payload = new URLSearchParams(body || {});
    }
    payload.set('_csrf', csrf());
    return fetch(url, {
        method: 'POST',
        body: payload,
        credentials: 'same-origin',
        headers: { Accept: 'application/json', 'X-Cal-Client': CLIENT_ID },
    }).then((r) => {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
    });
}

export const api = {
    create: (body) => send('/calendar/events', body),
    update: (id, body) => send('/calendar/events/' + enc(id) + '/update', body),
    remove: (id, body) => send('/calendar/events/' + enc(id) + '/delete', body || {}), // body: 반복 범위 {series_scope}
    restore: (id) => send('/calendar/events/' + enc(id) + '/restore', {}),
    patch: (id, fields) => send('/calendar/events/' + enc(id) + '/patch', fields),
    // 휴지통 (2026-09-18): 영구 삭제 하나 · 범위(cal) 비우기 — 둘 다 실행 취소 밖.
    purge: (id) => send('/calendar/events/' + enc(id) + '/purge', {}),
    emptyTrash: (cal) => send('/calendar/trash/empty', cal ? { cal } : {}),
};

// 다른 모듈(CRM/ERP)용 API: 같은 CSRF·헤더·JSON 규약으로
// POST base / base/:id/patch / base/:id/delete / base/:id/restore 를 친다.
// (update 는 patch 와 같은 엔드포인트 — 전체 폼도 patch 로 보낸다.)
export function makeApi(base) {
    const root = String(base || '').replace(/\/+$/, '');
    const url = (id, action) => root + '/' + enc(id) + '/' + action;
    return {
        create: (form) => send(root, form),
        patch: (id, form) => send(url(id, 'patch'), form),
        update: (id, form) => send(url(id, 'patch'), form),
        remove: (id) => send(url(id, 'delete'), {}),
        restore: (id) => send(url(id, 'restore'), {}),
    };
}

// ── Commands ──────────────────────────────────────────────────────────
// A created event keeps its id across undo (soft delete) and redo
// (restore), so the stack never has to re-create anything. The trailing
// `a` picks the API (default: the calendar one; CRM/ERP pass makeApi(...)).
export function createCommand(id, a) {
    const x = a || api;
    return { do: () => x.restore(id), undo: () => x.remove(id) };
}
export function deleteCommand(id, a) {
    const x = a || api;
    return { do: () => x.remove(id), undo: () => x.restore(id) };
}
export function patchCommand(id, before, after, a) {
    const x = a || api;
    return { do: () => x.patch(id, after), undo: () => x.patch(id, before) };
}
// Full-form update; `after` is mutable so a modal's autosaves coalesce into
// one history entry that always redoes the latest state.
export function updateCommand(id, before, after, a) {
    const x = a || api;
    const cmd = { before, after };
    cmd.do = () => x.update(id, cmd.after);
    cmd.undo = () => x.update(id, cmd.before);
    return cmd;
}
// Runs members one after another: parallel writes would contend for the
// same SQLite lock, and the order is what makes undo symmetric.
function sequence(fns) {
    return fns.reduce((p, fn) => p.then(fn), Promise.resolve());
}
export function composite(cmds) {
    return {
        do: () => sequence(cmds.map((c) => () => c.do())),
        undo: () => sequence(cmds.slice().reverse().map((c) => () => c.undo())),
    };
}

// ── Stack ─────────────────────────────────────────────────────────────
const LIMIT = 100;

class History {
    constructor() {
        this.stack = [];
        this.index = 0; // number of applied commands
        this.busy = false;
        this.listeners = [];
    }
    onChange(fn) {
        this.listeners.push(fn);
        fn(this);
    }
    emit() {
        this.listeners.forEach((fn) => fn(this));
    }
    push(cmd) {
        this.stack.length = this.index; // drop the redo tail
        this.stack.push(cmd);
        if (this.stack.length > LIMIT) this.stack.shift();
        this.index = this.stack.length;
        this.emit();
    }
    canUndo() {
        return !this.busy && this.index > 0;
    }
    canRedo() {
        return !this.busy && this.index < this.stack.length;
    }
    undo() {
        if (!this.canUndo()) return Promise.resolve(false);
        const cmd = this.stack[this.index - 1];
        this.busy = true;
        this.emit();
        return cmd.undo()
            .then(() => { this.index -= 1; return true; })
            .finally(() => { this.busy = false; this.emit(); });
    }
    redo() {
        if (!this.canRedo()) return Promise.resolve(false);
        const cmd = this.stack[this.index];
        this.busy = true;
        this.emit();
        return cmd.do()
            .then(() => { this.index += 1; return true; })
            .finally(() => { this.busy = false; this.emit(); });
    }
}

export const history = new History();
window.__calHistory = history; // 디버그·브라우저 테스트용 (스택 길이 확인)

// ── Labels: #calendarModal data-* (calendar pages) → the first [data-msg-undo] element
// (CRM/ERP put them on the section sidebar, which survives SPA swaps) → <body> data-* ──
export function labels() {
    const el = document.getElementById('calendarModal') || document.querySelector('[data-msg-undo]') || document.body;
    const d = (el && el.dataset) || {};
    return {
        undo: d.msgUndo || '실행 취소',
        redo: d.msgRedo || '다시 실행',
        undone: d.msgUndone || '실행 취소했습니다',
        redone: d.msgRedone || '다시 실행했습니다',
        failed: d.msgFailed || '저장에 실패했습니다',
        created: d.msgCreated || '일정을 추가했습니다',
        deleted: d.msgDeleted || '일정을 삭제했습니다',
        duplicated: d.msgDuplicated || '일정을 복제했습니다',
        moved: d.msgMoved || '일정을 이동했습니다',
        resized: d.msgResized || '일정 기간을 변경했습니다',
        bulkDeleted: d.msgBulkDeleted || '일정 %d개를 삭제했습니다',
        bulkMoved: d.msgBulkMoved || '일정 %d개를 이동했습니다',
        bulkUpdated: d.msgBulkUpdated || '일정 %d개를 변경했습니다',
        ownerOnly: d.msgOwnerOnly || '내가 만든 일정만 삭제됩니다',
        hidden: d.msgHidden || '일정을 숨겼습니다',
        unhidden: d.msgUnhidden || '숨기기를 해제했습니다',
        bulkHidden: d.msgBulkHidden || '일정 %d개를 숨겼습니다',
        done: d.msgDone || '완료로 표시했습니다',
        // 완료 체크를 푼 토스트 — 실행 취소의 undone 과 다른 키다(같은 이름이면 HTML 은 앞 속성을,
        // 객체 리터럴은 뒤 키를 남겨 두 토스트가 한 문장이 됐다).
        notDone: d.msgNotDone || '완료를 해제했습니다',
        quickPh: d.quickPh || '제목 입력 후 Enter',
        quickHint: d.quickHint || 'Enter 추가 · Tab 다음 날 · Ctrl+Enter 상세 · Esc 닫기',
        selected: d.selectedCount || '%d개 선택',
    };
}

export function fmtCount(pattern, n) {
    return pattern.replace('%d', String(n));
}

// ── Grid refresh ──────────────────────────────────────────────────────
// Re-fetch the current URL and swap #calGrid's content; listeners live on
// the grid element / document so they survive. Pages without the month
// grid (project hub) fall back to a full reload.
const REFRESH_ROOTS = ['calGrid', 'calTimeGrid', 'projHub', 'calList', 'calBoard', 'crmBoard', 'recList', 'crmAccount', 'crmReports', 'crmContact', 'crmActivities', 'crmSettings', 'erpDoc', 'erpApprovals', 'erpAccounts', 'hrBoard', 'hrTeamCal', 'hrOrg', 'hrRecList', 'hrPeople', 'workHub', 'workList', 'evalList'];

let refreshSeq = 0;
// navigate 가 부른 페이지 교체(opts.crossPath)가 진행 중이면 그 refreshSeq — 스타일을 기다리는 동안
// (adoptHeadStyles, 최대 1.5초) 들어온 배경 갱신이 그 교체를 밀어내지 않게 한다. 0 이면 없음.
let crossPending = 0;
// 교체를 기다리는 동안 배경 갱신을 떨궜다 — 새 화면의 HTML 은 그 갱신보다 먼저 받은 것이다 (R31).
let droppedRefresh = false;
// …그리고 그 뒤 페이지 교체가 끝났다: navigate 가 afterSwap·calendar:navigated 다음에 한 번 다시 받는다.
let deferredRefresh = false;
// pageSwapPending: navigate 가 부른 페이지 교체가 아직 끝나지 않았다 — 화면은 아직 currentPath 의 페이지가 아니다.
export function pageSwapPending() { return crossPending !== 0; }
// takeDeferredRefresh: 끝난 페이지 교체가 떨군 갱신 한 번을 빚지고 있으면 true (한 번만 — 꺼내면 지운다).
export function takeDeferredRefresh() { const d = deferredRefresh; deferredRefresh = false; return d; }
const FLIP_ROW = '.calp-tr[data-id], .cal-list-row[data-id], .rec-row[data-id], .kb-card[data-id]';
// 뷰 전환(월↔목록)으로 밀려난 루트는 리스너를 지키려고 여기 숨겨 둔다 (id 는 떼어 둔다)
function spaPool() {
    let pool = document.getElementById('spaPool');
    if (!pool) { pool = document.createElement('div'); pool.id = 'spaPool'; pool.hidden = true; document.body.appendChild(pool); }
    return pool;
}
// 클라이언트가 붙이는 상태 클래스는 갱신 뒤에도 유지한다 (선택·열 숨김은 리스너가 다시 맞춘다)
const KEEP_CLASSES = ['has-selection', 'has-hidden-cols', 'show-discount'];
function copyAttrs(from, to) {
    Array.from(to.attributes).forEach((a) => { if (a.name.startsWith('data-') && !a.name.startsWith('data-spa') && !from.hasAttribute(a.name)) to.removeAttribute(a.name); });
    Array.from(from.attributes).forEach((a) => { if (a.name.startsWith('data-') || a.name === 'style') to.setAttribute(a.name, a.value); });
    // class 도 최신으로 (쿼리에 따라 바뀌는 루트 수식 클래스: has-reason, no-owner …)
    const keep = KEEP_CLASSES.filter((c) => to.classList.contains(c));
    if (from.getAttribute('class') !== null) to.setAttribute('class', from.getAttribute('class'));
    keep.forEach((c) => to.classList.add(c));
}
// ── 섹션 안 다른 경로로의 이동 (CRM/ERP: 목록 ↔ 레코드 페이지 ↔ 편집기) ──────────
// 전역 내비(.navbar)와 섹션 사이드바(#adminSidebar, 접힘·스크롤 상태 유지)만 남기고 페이지를
// 통째로 새 문서 것으로 바꾼다: main.admin-main 과 .dashboard-container 의 나머지 자식
// (사이드 패널·선택바 — id 로 짝지어 교체, 사라진 것은 제거, 새 것은 추가).
// 모듈은 `calendar:navigated` 의 detail.crossPath 로 새 DOM 에 다시 붙는다.
export const PAGE_CONTAINER = 'main.admin-main';
function swapRegions(doc) {
    doc.querySelectorAll('[data-spa-region]').forEach((freshR) => {
        const key = freshR.getAttribute('data-spa-region');
        const cur = document.querySelector('[data-spa-region="' + key + '"]');
        if (!cur) return;
        // 편집 중인 리전(data-spa-hold 안 — 모바일 「전체」 의 탭 편집)은 갈지 않고 새 내용을 맡겨 둔다.
        // 편집이 끝나면 releaseRegionHold 가 그것을 넣는다 (일정 화면의 실시간 새로고침이 편집을 되돌리던 것, M3).
        // 아래 입력 포커스 검사보다 먼저 본다 — 체크박스에 포커스가 있어도 맡겨 둔 내용은 가장 새것이어야 한다.
        if (cur.closest('[data-spa-hold]')) { cur.__spaPending = freshR.innerHTML; return; }
        const a = document.activeElement; // 입력 중인 칸(검색어 등)은 건드리지 않는다
        if (a && cur.contains(a) && (a.tagName === 'INPUT' || a.tagName === 'TEXTAREA' || a.tagName === 'SELECT' || a.isContentEditable)) return;
        if (cur.innerHTML !== freshR.innerHTML) cur.innerHTML = freshR.innerHTML;
    });
}
// data-spa-hold 를 풀고, 그동안 밀려 있던 리전 내용이 있으면 넣는다.
export function releaseRegionHold(el) {
    if (!el) return;
    el.removeAttribute('data-spa-hold');
    const regions = [el, ...el.querySelectorAll('[data-spa-region]')].filter((r) => r.hasAttribute('data-spa-region'));
    for (const r of regions) {
        if (typeof r.__spaPending !== 'string') continue;
        const html = r.__spaPending;
        delete r.__spaPending;
        if (r.innerHTML !== html) r.innerHTML = html;
    }
}
// 사이드바는 바꾸지 않고(접힘·스크롤 상태 유지) 활성 항목·열린 그룹·배지만 새
// 문서에 맞춘다. 전역 사이드바(spec 2026-09-13 §3.1)의 data-sidebar 는 상수
// "global" 이라 노드 통째 교체는 더 이상 일어나지 않지만, 분기는 남겨 둔다
// (/apps 의 앱 레일은 data-sidebar 가 없어 값이 다르다).
export function syncSidebar(doc) {
    const cur = document.getElementById('adminSidebar');
    const fresh = doc.getElementById('adminSidebar');
    if (!cur || !fresh) return;
    if ((cur.dataset.sidebar || '') !== (fresh.dataset.sidebar || '')) {
        cur.replaceWith(document.adoptNode(fresh));
        return;
    }
    const freshItems = Array.from(fresh.querySelectorAll('.admin-sidebar-item'));
    cur.querySelectorAll('.admin-sidebar-item').forEach((a) => {
        const f = freshItems.find((x) => x.getAttribute('href') === a.getAttribute('href'));
        if (!f) return;
        const on = f.classList.contains('active');
        a.classList.toggle('active', on);
        if (on) a.setAttribute('aria-current', 'page'); else a.removeAttribute('aria-current');
    });
    // 활성 그룹만 새 문서에 맞춰 연다 (Task 1b, 2026-09-13 사용자 결정: 여러
    // 그룹이 동시에 열릴 수 있으므로 다른 그룹의 data-open 은 건드리지 않는다
    // — 사용자가 연 그룹이나 이 세션에서 이미 활성이었던 그룹은 그대로
    // 남는다. 새로고침해야 활성 그룹 + 기억된 집합만으로 다시 정리된다
    // (partials/global-sidebar.html 의 부트스트랩 스크립트).
    // 서버가 이미 판정한 결과(활성 그룹 하나)를 그대로 베낀다 — 여기서
    // 다시 계산하면 활성 항목이 없는 화면(프로필 등)에서 잘못 판정할 수 있다.
    const freshActive = fresh.querySelector('.gnav-group[data-group][data-open]');
    if (freshActive) {
        const g = cur.querySelector('.gnav-group[data-group="' + CSS.escape(freshActive.dataset.group) + '"]');
        if (g) {
            g.setAttribute('data-open', '1');
            const head = g.querySelector(':scope > .gnav-head');
            if (head) head.setAttribute('aria-expanded', 'true');
        }
    }
    // 모바일 드로어의 현재 모듈(data-m-current, 드로어 맨 위에 오는 그룹)과 그 아래 「다른 메뉴」 구분선도
    // 새 문서를 따른다 (M10) — 구분선은 현재 모듈이 있는 화면에만 있다.
    cur.querySelectorAll('.gnav > [data-group]').forEach((g) => {
        const f = fresh.querySelector('.gnav > [data-group="' + CSS.escape(g.dataset.group) + '"]');
        if (f && f.hasAttribute('data-m-current')) g.setAttribute('data-m-current', '1'); else g.removeAttribute('data-m-current');
    });
    const curOthers = cur.querySelector('.gnav > .gnav-m-others');
    const freshOthers = fresh.querySelector('.gnav > .gnav-m-others');
    if (curOthers && !freshOthers) curOthers.remove();
    else if (!curOthers && freshOthers) cur.querySelector('.gnav')?.appendChild(document.importNode(freshOthers, true));
    // 배지만 텍스트를 갈아끼운다. 예전에는 사이드바 안의 모든 [id] 를 돌며
    // textContent 를 맞췄는데, 전역 사이드바에는 <ul id="gnav-…"> 가 있어
    // 그 방식이면 목록 전체가 텍스트 노드 하나로 뭉개진다.
    fresh.querySelectorAll('[id][data-nav-count]').forEach((f) => {
        const c = cur.querySelector('#' + CSS.escape(f.id));
        if (!c || c.tagName !== f.tagName) return;
        if (c.textContent !== f.textContent) c.textContent = f.textContent;
        c.hidden = f.hidden;
    });
}
// 헤더 모드의 모듈 탭 줄(.navbar-tabs)을 새 문서에 맞춘다. swapPage 는 .navbar
// 를 통째로 건너뛰므로(PAGE_EXTRA_SKIP) 모듈 공개가 바뀌어도 탭 줄만 옛날
// 것으로 남았다 — 여기서 innerHTML 한 번으로 갈아끼운다. 사이드바 모드에는
// 탭 줄이 없으므로(NavTabs 없음) 조용한 no-op 다.
export function syncNavTabs(doc) {
    const cur = document.querySelector('.navbar-tabs');
    const fresh = doc.querySelector('.navbar-tabs');
    if (!cur && !fresh) return;
    if (cur && fresh) {
        if (cur.innerHTML !== fresh.innerHTML) cur.innerHTML = fresh.innerHTML;
    } else if (fresh) {
        // 탭 줄은 템플릿에서 아이콘 스프라이트 뒤, 햄버거 버튼 앞에 있다.
        const bar = document.querySelector('.navbar .navbar-inner') || document.querySelector('.navbar');
        const before = bar && bar.querySelector('.hamburger-menu');
        if (!bar) return;
        const node = document.adoptNode(fresh.cloneNode(true));
        if (before) bar.insertBefore(node, before); else bar.appendChild(node);
    } else {
        cur.remove();
    }
    if (window.fitNavTabs) window.fitNavTabs();
}
// 교체에서 빼는 껍데기·비활성 조각. 실행되는 <script> 는 옮겨 붙여도 다시 돌지 않으므로
// 건드리지 않지만, JSON 데이터 섬(<script type="application/json">)은 페이지 데이터라 갈아끼운다.
const PAGE_EXTRA_SKIP = '.admin-layout, nav, .navbar, script:not([type="application/json"]), style, link, template';
// 페이지가 <body> 안에 직접 그리는 <style> (관리자 화면 상당수가 그렇다). 인라인
// <script> 와 달리 스타일은 옮겨 붙이면 그대로 적용되므로, 떠나는 페이지 것은 걷어내고
// 새 페이지 것을 붙인다 — 안 하면 새 화면이 옛 화면의 스타일로 그려진다.
// 새 main.admin-main 안의 스타일은 이미 컨테이너와 함께 따라왔으니 건너뛴다.
function swapPageStyles(doc) {
    const main = document.querySelector(PAGE_CONTAINER);
    document.querySelectorAll('body style').forEach((el) => { if (!main || !main.contains(el)) el.remove(); });
    doc.querySelectorAll('body style').forEach((el) => { if (!el.closest(PAGE_CONTAINER)) document.body.appendChild(document.adoptNode(el)); });
}
// 왼쪽 열이 **있는가**: 없음(#adminSidebar 자체가 없다) / 숨김(헤더 모드에서
// 현재 그룹이 없는 화면의 data-nav-empty — CSS 가 display:none 한다) / 있음.
// 제자리 교체는 이 상태가 같을 때만 옳다: 열이 있는 화면에서 없는 화면으로
// (또는 그 반대로) 본문만 갈아끼우면 왼쪽 열이 사라지거나 생기면서 본문이
// 통째로 옆으로 뛴다("흠칫"). 그런 이동은 제자리 교체로 얻을 게 없으므로 진짜
// 내비게이션으로 넘긴다 — 브라우저가 새 문서를 한 번에 그린다.
//
// 사이드바 **종류**(global ↔ admin)는 여기서 보지 않는다: 두 사이드바는 폭이
// 같아 syncSidebar 의 노드 교체가 본문을 움직이지 않는다(프로브로 확인 —
// /admin/config ↔ /admin/attendance/settings 왕복 내내 aside 260px · main x 260
// 고정). 그 경로는 admin-spa.mjs M3 이 지키는 그대로 둔다.
function asideSignature(root) {
    const a = root.getElementById ? root.getElementById('adminSidebar') : root.querySelector('#adminSidebar');
    if (!a) return 'none';
    return a.hasAttribute('data-nav-empty') ? 'hidden' : 'shown';
}
// 배포가 바뀌었는가: <meta name="pwa-version"> 은 내장 정적 파일 전체의 해시라, 새 문서의 값이
// 지금 문서와 다르면 이 화면이 돌리는 JS(묶음)는 옛것이다. 옛 JS 로 새 마크업을 돌리지 않도록
// 그런 이동도 진짜 내비게이션으로 넘겨 새 묶음을 싣는다.
function buildOf(root) {
    const m = root.querySelector('meta[name="pwa-version"]');
    return m ? m.getAttribute('content') || '' : '';
}
function needsFullLoad(doc) {
    if (asideSignature(document) !== asideSignature(doc)) return true;
    const cur = buildOf(document);
    const next = buildOf(doc);
    return !!(cur && next && cur !== next);
}
// 새 문서의 <head> 스타일시트(화면별 ExtraCSS — orgchart.css·hr-workforce.css …) 중 지금 문서에
// 아직 없는 것을 붙이고, 불러올 때까지(최대 1.5초) 기다린다 — 교체된 화면이 스타일 없이 한 번
// 그려지지(가로 스크롤·깨진 배치) 않게. swapPage 는 <head> 를 건드리지 않으므로 이것이 없으면
// 섹션 안 이동으로 도착한 화면에는 그 화면의 CSS 가 없었다. 더하기만 한다: 떠나는 화면의 것은
// 그대로 둔다(다음 방문에 다시 받지 않고, 다른 화면의 규칙은 그 화면의 클래스에만 걸린다).
// 이미 있는지는 **경로**로 가린다(head-styles.js newStyleIndexes — ?v= 꼬리는 서버가 뜰 때마다
// 바뀌므로 전체 주소로 비교하면 재시작 뒤 첫 이동이 전역 시트를 <head> 끝에 한 벌 더 붙여 캐스케이드
// 순서를 뒤집었다, R30). 이미 있는 시트는 붙이지도·옮기지도·주소를 바꾸지도 않는다.
const HEAD_STYLE_WAIT_MS = 1500;
function adoptHeadStyles(doc) {
    if (needsFullLoad(doc)) return Promise.resolve(); // 어차피 진짜 내비게이션
    const hrefOf = (l) => l.getAttribute('href') || '';
    const have = Array.from(document.querySelectorAll('head link[rel~="stylesheet"]'), hrefOf);
    const want = Array.from(doc.querySelectorAll('head link[rel~="stylesheet"]'));
    const waits = newStyleIndexes(have, want.map(hrefOf), window.location.href).map((i) => {
        const node = document.importNode(want[i], true);
        const loaded = new Promise((resolve) => {
            node.addEventListener('load', resolve, { once: true });
            node.addEventListener('error', resolve, { once: true });
        });
        document.head.appendChild(node);
        return loaded;
    });
    if (!waits.length) return Promise.resolve();
    return Promise.race([Promise.all(waits), new Promise((resolve) => setTimeout(resolve, HEAD_STYLE_WAIT_MS))]);
}
function swapPage(doc, o = {}) {
    if (needsFullLoad(doc)) {
        window.location.assign(window.location.href);
        return true; // 페이지는 곧 사라진다 — 호출자는 여기서 멈춘다
    }
    const curMain = document.querySelector(PAGE_CONTAINER);
    const freshMain = doc.querySelector(PAGE_CONTAINER);
    if (!curMain || !freshMain) throw new Error('no page container');
    // E-18 §4.1: 같은 화면을 다시 그릴 때는 스크롤과 (id 가 있는) 포커스를 잇는다.
    const prevTop = curMain.scrollTop;
    const act = document.activeElement;
    const focusId = o.keepMainScroll && act && act.id && curMain.contains(act) ? act.id : '';
    const pool = document.getElementById('spaPool');
    if (pool) pool.innerHTML = ''; // 옛 페이지에서 풀에 넣어 둔 루트는 더 쓸 일이 없다
    document.body.classList.remove('modal-scroll-lock'); // 열려 있던 패널·시트는 페이지와 함께 사라진다
    curMain.replaceWith(document.adoptNode(freshMain));
    const curShell = document.querySelector('.dashboard-container');
    const freshShell = doc.querySelector('.dashboard-container');
    if (curShell && freshShell) {
        const isExtra = (el) => el.nodeType === 1 && !el.matches(PAGE_EXTRA_SKIP);
        const freshExtras = Array.from(freshShell.children).filter(isExtra);
        Array.from(curShell.children).filter(isExtra).forEach((el) => { if (!el.id || !freshExtras.some((f) => f.id === el.id)) el.remove(); });
        freshExtras.forEach((f) => {
            const cur = f.id ? document.getElementById(f.id) : null;
            const node = document.adoptNode(f);
            if (cur && curShell.contains(cur)) cur.replaceWith(node); else curShell.appendChild(node);
        });
        // 페이지 종류 클래스(crm-page/erp-page …)도 새 문서에 맞춘다
        if (freshShell.getAttribute('class') !== null) curShell.setAttribute('class', freshShell.getAttribute('class'));
    }
    swapPageStyles(doc);
    swapRegions(doc);
    syncSidebar(doc);
    syncNavTabs(doc);
    if (doc.title) document.title = doc.title;
    // data-page: 페이지별 모듈 등록부(main.js registerAdminPage)가 읽는 키
    if (doc.body && doc.body.dataset.page !== undefined) document.body.dataset.page = doc.body.dataset.page;
    const main = document.querySelector(PAGE_CONTAINER);
    if (main) main.scrollTop = o.keepMainScroll ? prevTop : 0;
    if (focusId) {
        const again = document.getElementById(focusId);
        if (again && main && main.contains(again)) again.focus({ preventScroll: true });
    }
    document.dispatchEvent(new CustomEvent('calendar:page-swapped', { detail: { path: window.location.pathname, container: main } }));
}

// 교체 **직전**의 본문 스크롤 — 받는 동안 사용자가 옮긴 자리까지 담는다. 같은 경로 다시 그리기의
// 라우터가 페이지 모듈이 붙은 뒤 이 자리로 한 번 더 맞춘다(백로그 BH5 — 예전에는 받기 전에 잰
// 자리로 되돌렸다). 한 번 읽으면 비운다.
let swapScroll = null;
export function takeSwapScroll() {
    const v = swapScroll;
    swapScroll = null;
    return v;
}

// opts.html: 이미 받아 둔 문서(프리페치)가 있으면 그것을 쓴다.
// opts.crossPath: 경로가 바뀐 이동 — 루트 대신 페이지 컨테이너를 통째로 교체한다.
export function refreshGrid(opts) {
    opts = opts || {};
    // 같은 경로의 갱신은 REFRESH_ROOTS 순서의 첫 루트만 갈아끼운다 (거래처 페이지의 #recList 처럼 안쪽 목록;
    // 바깥 #crmAccount 는 요소 리스너를 지녀 innerHTML 교체 대상이 아니다). 경로가 바뀌면 페이지 전체를 바꾼다.
    const curId = REFRESH_ROOTS.find((id) => document.getElementById(id));
    // 부분 교체할 루트가 아예 없는 화면(관리자 화면들)은 같은 경로의 갱신(필터 GET 폼·
    // 같은 항목 다시 클릭)도 페이지 통째 교체로 처리한다 — 그러지 않으면 여기서 거절되어
    // navigate() 의 안전망이 전체 새로고침으로 떨어진다.
    if (!curId && !document.querySelector(PAGE_CONTAINER)) return Promise.reject(new Error('no refreshable root'));
    // 길은 planRefresh(refresh-plan.js) 하나가 정한다:
    //  - 페이지 교체가 기다리는 동안 들어온 배경 갱신(편집 뒤 scheduleRefresh·refreshOrReload, 패널의
    //    충돌 다시 읽기 — 모두 opts 없이 부른다)은 순번을 올리지 않고 끝낸다: 올리면 기다리던 교체가
    //    취소되는데 navigate 는 그것을 모른 채 afterSwap·calendar:navigated(crossPath) 를 올려 주소는
    //    새 화면, 화면은 옛 문서로 남았다 (R30). 다만 새 화면의 HTML 은 이 갱신보다 먼저 받은 것이라
    //    droppedRefresh 로 적어 두고, 교체가 끝난 뒤 navigate 가 한 번 다시 받는다 (R31).
    //  - 교체를 기다리는 중에 navigate 가 부른 갱신(같은 링크 두 번 누르기 등)은 루트 교체가 아니라
    //    언제나 페이지 교체다 — 화면이 아직 currentPath 의 페이지가 아니라 옛 껍데기에 새 루트를 심으면
    //    두 화면이 섞였다 (R31).
    //  - 루트가 없는 화면에서 navigate 가 부른 갱신도 페이지 교체이므로 "기다리는 중" 으로 적는다 (R31).
    const plan = planRefresh({ hasRoot: !!curId, crossPath: !!opts.crossPath, alive: !!opts.alive, pending: crossPending !== 0 });
    if (plan.action === 'drop') { droppedRefresh = true; return Promise.resolve(); }
    // 앞선 교체가 남긴 값은 버린다 — 이 갱신이 교체하면 다시 재고, 앞질려 교체하지 않으면 비어 있어야
    // 그 다시 그리기가 옛 화면의 스크롤로 뛰지 않는다(최종 리뷰 4). 위에서 떨군 갱신은 아무것도 하지
    // 않으므로 이 값도 건드리지 않는다 — 기다리던 페이지 교체가 swapPage 직전에 잰 값을 그 교체가
    // 끝나기(settle) 전에 들어온 배경 갱신이 지우면 navigate 가 되맞출 자리를 잃는다.
    swapScroll = null;
    const crossPath = plan.action === 'page';
    const seq = ++refreshSeq;
    if (plan.pend) crossPending = seq;
    const settle = () => { if (crossPending === seq) crossPending = 0; };
    const load = opts.html ? Promise.resolve(opts.html) : fetch(window.location.href, { credentials: 'same-origin', headers: { 'X-Requested-With': 'fetch' } })
        .then((r) => {
            if (!r.ok) throw new Error('refresh failed');
            return r.text();
        });
    return load.then((html) => {
            if (seq !== refreshSeq) { settle(); return; } // 더 새 갱신이 이미 시작됨 — 이 응답은 버린다
            const doc = new DOMParser().parseFromString(html, 'text/html');
            // swapPage 가 진짜 내비게이션으로 넘겼으면(왼쪽 열 상태가 다른 화면)
            // 이 체인은 여기서 멈춘다 — 뒤따르는 afterSwap·스크롤·이벤트는 곧
            // 사라질 문서를 건드릴 뿐이다.
            if (crossPath) {
                // 새 화면의 <head> 스타일을 먼저 붙이고(불러올 때까지 짧게 기다린 뒤) 교체한다.
                return adoptHeadStyles(doc).then(() => {
                    // 기다리는 사이 더 새 이동이 시작됐다(뒤로 가기·다른 링크): navigate 는 navSeq 를 곧바로
                    // 올리지만 refreshGrid 는 제 fetch 가 끝난 뒤에야 부르므로 refreshSeq 만으로는 모른다
                    // (calendar-spa.js navigate 의 주석). 교체를 버린다 — 이 옛 화면의 page-swapped 는
                    // 없고, afterSwap·calendar:navigated 는 navigate 의 navSeq 가드가 막는다 (R30).
                    // 떨군 갱신(droppedRefresh)은 지우지 않는다: 그 뒤의 이동이 이 갱신보다 먼저 HTML 을
                    // 받았을 수 있으니 그 이동의 교체가 끝난 뒤 다시 받게 넘긴다.
                    if (opts.alive && !opts.alive()) return;
                    if (seq !== refreshSeq) {
                        // 살아 있는 이동의 교체가 다른 갱신에 밀렸다 — 주소는 이미 새 화면이라 조용히 접으면
                        // 화면만 옛 문서로 남는다. 던져서 navigate 가 진짜 이동으로 떨어지게 한다.
                        if (opts.alive) throw new Error('page swap superseded');
                        return;
                    }
                    // 교체 **직전**의 본문 스크롤(BH5) — 스타일을 기다리는 동안 옮긴 자리까지 담는다.
                    swapScroll = document.querySelector(PAGE_CONTAINER)?.scrollTop ?? null;
                    if (swapPage(doc, { keepMainScroll: !!opts.keepMainScroll })) { settle(); droppedRefresh = false; return new Promise(() => {}); }
                    // 교체가 끝났다 — 기다리는 동안 떨군 갱신이 있으면 navigate 가 calendar:navigated 뒤에 한 번 받는다.
                    if (opts.alive && droppedRefresh) { droppedRefresh = false; deferredRefresh = true; }
                }).catch((err) => {
                    // 실패하면 navigate 가 진짜 이동으로 떨어진다 — 새로 받는 문서가 최신이니 빚은 지운다.
                    if (opts.alive) droppedRefresh = false;
                    throw err;
                });
            }
            swapScroll = document.querySelector(PAGE_CONTAINER)?.scrollTop ?? null; // 교체 직전의 본문 스크롤 (BH5)
            const newId = REFRESH_ROOTS.find((id) => doc.getElementById(id));
            if (!newId) throw new Error('no root in response');
            const fresh = doc.getElementById(newId);
            let grid = document.getElementById(curId);
            let added = false;
            // 갱신 전 포커스가 루트 안의 행/카드에 있었다면 같은 data-id 로 돌려준다 (Alt+←/→·S·O 연속 조작, QA ux-5)
            const act = document.activeElement;
            const focusRow = act && act.closest && grid.contains(act) ? act.closest('[data-id]') : null;
            const focusId = focusRow ? focusRow.dataset.id : '';
            const focusSort = act && act.closest && act.closest('[data-sort]')?.dataset.sort;
            const hadFocus = !!(act && act !== document.body && grid.contains(act));
            if (newId !== curId) {
                // 뷰 전환: 옛 루트는 풀로, 새 루트는 같은 자리에 (풀에 있던 것이면 재사용 → 리스너 유지)
                const pooled = spaPool().querySelector('[data-spa-id="' + newId + '"]');
                const target = pooled || document.adoptNode(fresh);
                grid.parentNode.insertBefore(target, grid);
                grid.dataset.spaId = curId;
                grid.removeAttribute('id');
                grid.innerHTML = '';
                spaPool().appendChild(grid);
                if (pooled) { pooled.id = newId; delete pooled.dataset.spaId; grid = pooled; }
                else { grid = target; added = true; }
            }
            if (!added) {
                // FLIP: 갱신 전 행 위치를 기억해 두고, 갱신 뒤 제자리로 미끄러지게 한다 (재정렬 튐 방지)
                const before = {};
                grid.querySelectorAll(FLIP_ROW).forEach((r) => { before[r.dataset.id] = r.getBoundingClientRect().top; });
                grid.innerHTML = fresh.innerHTML;
                const moved = [];
                grid.querySelectorAll(FLIP_ROW).forEach((r) => {
                    const prev = before[r.dataset.id];
                    if (prev == null) return;
                    const dy = prev - r.getBoundingClientRect().top;
                    if (Math.abs(dy) > 1) { r.style.transform = 'translateY(' + dy + 'px)'; moved.push(r); }
                });
                if (moved.length) {
                    requestAnimationFrame(() => { moved.forEach((r) => { r.classList.add('flip'); r.style.transform = ''; }); setTimeout(() => moved.forEach((r) => r.classList.remove('flip')), 400); });
                }
                // 루트의 data-*/style (스팬 길이·정렬 모드 등)도 최신으로 — 내부만 바꾸면 드래그 환산이 옛 값을 쓴다.
                copyAttrs(fresh, grid);
            }
            if (hadFocus) {
                const again = (focusSort && grid.querySelector('[data-sort="' + CSS.escape(focusSort) + '"]')) || (focusId && grid.querySelector('.kb-card[data-id="' + focusId + '"], .rec-row[data-id="' + focusId + '"], .calp-tr[data-id="' + focusId + '"], .cal-list-row[data-id="' + focusId + '"]'))
                    || grid.querySelector('.rec-row[data-id], .kb-card[data-id]');
                if (again && again.focus) again.focus({ preventScroll: true });
            }
            // 부속 영역(제목·내비·필터·칩·집계): 같은 이름의 [data-spa-region] 을 통째로 갈아끼운다.
            swapRegions(doc);
            syncSidebar(doc);
            syncNavTabs(doc);
            if (doc.title) document.title = doc.title;
            if (added) document.dispatchEvent(new CustomEvent('calendar:root-added', { detail: { id: newId, root: grid } }));
            document.dispatchEvent(new CustomEvent('calendar:refreshed'));
        })
        .finally(settle);
}

export function refreshOrReload() {
    return refreshGrid().catch(() => {
        window.location.reload();
        return new Promise(() => {}); // never resolves: the page is going away
    });
}

// ── Toast ─────────────────────────────────────────────────────────────
let currentToast = null;

export function dismissToast() {
    if (currentToast) currentToast.remove();
    currentToast = null;
}

export function showToast(message, opts) {
    const o = opts || {};
    if (currentToast) currentToast.remove();
    const toast = document.createElement('div');
    toast.className = 'cal-undo-toast' + (o.error ? ' err' : '');
    toast.setAttribute('role', 'status');
    const msg = document.createElement('span');
    msg.textContent = message;
    toast.appendChild(msg);
    if (o.actionLabel && o.onAction) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.textContent = o.actionLabel;
        btn.addEventListener('click', () => {
            btn.disabled = true;
            o.onAction();
        });
        toast.appendChild(btn);
    }
    document.body.appendChild(toast);
    currentToast = toast;
    setTimeout(() => {
        if (toast.parentNode) toast.remove();
        if (currentToast === toast) currentToast = null;
    }, o.duration || 4000);
    return toast;
}

// ── Undo / redo with feedback ─────────────────────────────────────────
export function performUndo() {
    const L = labels();
    const cmd = history.stack[history.index - 1];
    refreshPending = false;
    clearTimeout(refreshTimer);
    editVersion += 1;
    return history.undo().then((ok) => {
        if (!ok) return false;
        return (cmd.refresh ? cmd.refresh() : refreshOrReload()).then(() => {
            showToast(L.undone, { actionLabel: L.redo, onAction: performRedo });
            return true;
        });
    }).catch(() => showToast(L.failed, { error: true }));
}

export function performRedo() {
    const L = labels();
    const cmd = history.stack[history.index];
    refreshPending = false;
    clearTimeout(refreshTimer);
    editVersion += 1;
    return history.redo().then((ok) => {
        if (!ok) return false;
        return (cmd.refresh ? cmd.refresh() : refreshOrReload()).then(() => {
            showToast(L.redone, { actionLabel: L.undo, onAction: performUndo });
            return true;
        });
    }).catch(() => showToast(L.failed, { error: true }));
}

// Apply a command now, record it, refresh, and offer undo in a toast.
export function commit(cmd, message) {
    const L = labels();
    return cmd.do()
        .then(() => {
            history.push(cmd);
            return refreshOrReload();
        })
        .then(() => {
            if (message) showToast(message, { actionLabel: L.undo, onAction: performUndo });
        })
        .catch(() => showToast(L.failed, { error: true }));
}

// ── 조용한 커밋 (낙관적 갱신) ────────────────────────────────────────
// 화면은 호출자가 먼저 바꿔 두고, 서버 반영 뒤 잠시 후 한 번만 소프트 갱신한다.
// 인라인 편집 입력이 열려 있는 동안엔 갱신을 미뤄 타이핑이 끊기지 않게 한다.
let refreshTimer = null;
let refreshPending = false;
// 편집 세대: 저장이 시작될 때마다 올라간다. 갱신 fetch 가 시작된 뒤 새 편집이
// 있었으면 그 응답은 낡은 것이므로 버리고 다시 예약한다 (좌우 이동 시 튐 방지).
let editVersion = 0;
let inflight = 0;
// 서버 쓰기는 순서대로: 같은 행에 대한 patch 가 뒤바뀌어 도착하지 않게.
let saveQueue = Promise.resolve();
function enqueue(fn) {
    const run = saveQueue.then(fn, fn);
    saveQueue = run.catch(() => {});
    return run;
}

function editingInline() {
    const a = document.activeElement;
    return !!(a && a.classList && a.classList.contains('calp-edit'));
}
// 드래그 중엔 목록을 갈아끼우지 않는다 (놓는 위치가 어긋나지 않게).
let dragDepth = 0;
export function beginDrag() { dragDepth += 1; }
export function endDrag() { dragDepth = Math.max(0, dragDepth - 1); if (refreshPending) scheduleRefresh(100); }
export function scheduleRefresh(delay) {
    refreshPending = true;
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(runPendingRefresh, delay == null ? 350 : delay);
}
// 표 위에 마우스가 있는 동안엔 재정렬로 행이 튀지 않게 갱신을 미룬다 (최대 6초).
let hoverSince = 0;
function hoveringList() {
    const root = document.getElementById('projHub') || document.getElementById('calList');
    if (!root || !root.matches(':hover')) { hoverSince = 0; return false; }
    if (!hoverSince) hoverSince = Date.now();
    return Date.now() - hoverSince < 6000;
}
function runPendingRefresh() {
    if (!refreshPending) return;
    if (editingInline() || inflight > 0 || dragDepth > 0 || hoveringList()) {
        refreshTimer = setTimeout(runPendingRefresh, 300);
        return;
    }
    hoverSince = 0;
    refreshPending = false;
    const v = editVersion;
    refreshGrid().then(() => {
        if (v !== editVersion) scheduleRefresh(150); // 그 사이 편집이 있었다면 한 번 더
    }).catch(() => { window.location.reload(); });
}
document.addEventListener('focusout', (e) => {
    if (refreshPending && e.target && e.target.classList && e.target.classList.contains('calp-edit')) {
        clearTimeout(refreshTimer);
        refreshTimer = setTimeout(runPendingRefresh, 120);
    }
});
function emitState(state) {
    document.dispatchEvent(new CustomEvent('calendar:save-state', { detail: state }));
}
export function commitQuiet(cmd, opts) {
    const o = opts || {};
    const L = labels();
    editVersion += 1;
    inflight += 1;
    emitState('saving');
    return enqueue(() => cmd.do())
        .then(() => {
            inflight -= 1;
            history.push(cmd);
            emitState('saved');
            if (o.refresh === 'now') { refreshPending = false; clearTimeout(refreshTimer); return refreshOrReload(); }
            scheduleRefresh(o.delay);
            return null;
        })
        .catch(() => {
            inflight -= 1;
            emitState('failed');
            showToast(L.failed, { error: true });
            return refreshOrReload();
        });
}
// 다른 사용자의 변경(SSE): 내 편집을 방해하지 않도록 조용한 갱신 경로를 탄다.
export function remoteChanged() {
    scheduleRefresh(500);
}

export function recordQuiet(cmd, opts) {
    const o = opts || {};
    editVersion += 1;
    history.push(cmd);
    emitState('saved');
    if (o.refresh === 'now') return refreshOrReload();
    scheduleRefresh(o.delay);
    return Promise.resolve();
}

// Record an already-applied command (create paths know the id only after
// the server answers), refresh, and offer undo.
export function record(cmd, message) {
    const L = labels();
    history.push(cmd);
    return refreshOrReload().then(() => {
        if (message) showToast(message, { actionLabel: L.undo, onAction: performUndo });
    });
}

function inTextField(target) {
    if (!target) return false;
    const tag = (target.tagName || '').toUpperCase();
    if (tag === 'INPUT') return !/^(checkbox|radio|button|submit|reset|range|color|file)$/i.test(target.type || 'text');
    return tag === 'TEXTAREA' || tag === 'SELECT' || !!target.isContentEditable;
}

// Toolbar buttons + Ctrl/⌘+Z / Ctrl/⌘+Shift+Z / Ctrl+Y. No-op on pages
// without a refreshable root (calendar, project hub, CRM/ERP lists).
export function hasRefreshRoot() {
    return REFRESH_ROOTS.some((id) => document.getElementById(id));
}
let historyReady = false;
export function setupCalendarHistory() {
    if (historyReady) return;
    historyReady = true;
    const undoBtn = document.getElementById('calUndoBtn');
    const redoBtn = document.getElementById('calRedoBtn');
    history.onChange((h) => {
        if (undoBtn) undoBtn.disabled = !h.canUndo();
        if (redoBtn) redoBtn.disabled = !h.canRedo();
    });
    if (undoBtn) undoBtn.addEventListener('click', performUndo);
    if (redoBtn) redoBtn.addEventListener('click', performRedo);

    document.addEventListener('keydown', (e) => {
        if (!hasRefreshRoot() || !(e.ctrlKey || e.metaKey) || e.altKey) return;
        if (inTextField(e.target)) return; // 텍스트 필드 안에서는 브라우저의 텍스트 undo
        const key = e.key.toLowerCase();
        if (key === 'z' && !e.shiftKey) {
            e.preventDefault();
            performUndo();
        } else if ((key === 'z' && e.shiftKey) || key === 'y') {
            e.preventDefault();
            performRedo();
        }
    });
}
